/* eslint-disable jira/naming/disallow-filenames */

import React from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import type { KBSettingsAppWithLayout as ProjectSettingsServiceDeskKnowledgeBaseV2Type } from '@jira/spa-apps__servicedesk-knowledge-settings/src/KBSettingsAppWithLayout.tsx';
import type SettingsServiceDeskKnowledgeBaseType from '@jira/spa-apps__project-settings-service-desk-knowledge-base/src/ExportedDefault.tsx';
import { HelpCenterSettingsRedirect } from '@jira/servicedesk__customer-service-common/src/HelpCenterSettingsRedirect.tsx';
import { PostOfficeProvider } from '@jira/post-office__post-office-provider/src/index.tsx';
import { markAsProductBundleReady } from '@jira/platform__spa/src/services/mark-as-product-bundle-ready/index.tsx';
import { LazyPage } from '@jira/platform__spa/src/view/lazy-page/index.tsx';
import { lazyForPaint, type LazyComponent } from '@atlassian/react-loosely-lazy';
import type {
	Location,
	Query,
	Route,
	Match,
	HistoryAction,
} from '@atlassian/react-resource-router';

export const NEXTGEN_SETTINGS = 'project/service-desk/settings/index';
export const CLASSIC_SETTINGS = 'project/service-desk/settings';

export const LazySettingsServiceDeskKnowledgeBase: LazyComponent<{
	(props: {
		location: Location;
		query: Query;
		route: Route;
		match: Match;
		action: HistoryAction;
	}): React.JSX.Element;
	displayName: string;
}> = lazyForPaint<typeof SettingsServiceDeskKnowledgeBaseType>(
	() =>
		import(
			/* webpackChunkName: "async-project-settings-service-desk-knowledge-base", jiraSpaEntry: "async-project-settings-service-desk-knowledge-base" */ '@jira/spa-apps__project-settings-service-desk-knowledge-base/src/ExportedDefault.tsx'
		),
);

export const LazyProjectSettingsServiceDeskKnowledgeBaseV2: LazyComponent<() => React.JSX.Element> =
	lazyForPaint<typeof ProjectSettingsServiceDeskKnowledgeBaseV2Type>(() =>
		markAsProductBundleReady(
			import(
				/* webpackChunkName: "async-project-settings-service-desk-knowledge-base-v2", jiraSpaEntry: "async-project-settings-service-desk-knowledge-base-v2" */ '@jira/spa-apps__servicedesk-knowledge-settings/src/ServicedeskKnowledgeSettings.tsx'
			),
		),
	);

export const ServiceDeskKnowledgeBaseV2 = (): React.JSX.Element =>
	fg('jsm_kb_agent_3p_connect_banner') ? (
		<HelpCenterSettingsRedirect page="kb">
			<PostOfficeProvider>
				<LazyPage
					Page={LazyProjectSettingsServiceDeskKnowledgeBaseV2}
					pageId="project-settings-knowledge-base"
					shouldShowSpinner
				/>
			</PostOfficeProvider>
		</HelpCenterSettingsRedirect>
	) : (
		<HelpCenterSettingsRedirect page="kb">
			<LazyPage
				Page={LazyProjectSettingsServiceDeskKnowledgeBaseV2}
				pageId="project-settings-knowledge-base"
				shouldShowSpinner
			/>
		</HelpCenterSettingsRedirect>
	);

const AsyncProjectSettingsServiceDeskKnowledgeBaseV2 = (): React.JSX.Element => (
	<LazyPage
		Page={LazySettingsServiceDeskKnowledgeBase}
		pageId="project-settings-knowledge-base"
		shouldShowSpinner
	/>
);

export default AsyncProjectSettingsServiceDeskKnowledgeBaseV2;
